import { Link, useLocation } from 'react-router-dom'
import { useState } from 'react'
import { useUser } from '../hooks/useUser'
import { useSidebarState } from '../hooks/useSidebarState'
import { stopImpersonating } from '../lib/api'
import {
  Home,
  AppWindow,
  Users,
  LayoutDashboard,
  LogOut,
  AlertTriangle,
  X,
  Activity,
  PanelLeftClose,
  PanelLeft,
  Menu,
  Sliders,
  BarChart3,
  Bot,
  Server,
  MessageCircle,
  Info,
  FileText,
  BookOpen,
  Globe,
  Settings,
  Lightbulb,
  LayoutTemplate,
  ConciergeBell,
  Shield,
} from 'lucide-react'
import { cn } from '../lib/utils'
import { ThemeToggleIcon } from './ThemeToggle'
import { useWhatsNew } from '../hooks/useWhatsNew'
import { WhatsNewDialog } from './WhatsNewDialog'
import HelpWidget from './HelpWidget'
import { openHelpChat } from '../lib/help'
import WelcomeModal from './WelcomeModal'
import BetaBadge from './BetaBadge'
import KeyboardShortcutsDialog from './KeyboardShortcutsDialog'
import { useWelcome } from '../hooks/useWelcome'
import { NotificationBell } from './NotificationPanel'
import { InstallPromptBanner } from './InstallPromptBanner'
import { UpdateBanner } from './UpdateBanner'

interface LayoutProps {
  children: React.ReactNode
}

const navItems = [
  { path: '/', label: 'Home', icon: Home },
  { path: '/apps', label: 'My Apps', icon: AppWindow },
  { path: '/browse', label: 'Browse', icon: Globe },
  { path: '/creator-dashboard', label: 'My Analytics', icon: BarChart3 },
  { path: '/settings', label: 'Settings', icon: Settings },
]

const docsItems = [
  { path: '/guide', label: 'Guide', icon: BookOpen },
  { path: '/changelog', label: 'Changelog', icon: FileText },
]

const adminNavItems = [
  { path: '/admin', label: 'Overview', icon: LayoutDashboard },
  { path: '/admin/users', label: 'Users', icon: Users },
  { path: '/admin/roles', label: 'Roles', icon: Shield },
  { path: '/admin/apps', label: 'Apps', icon: AppWindow },
  { path: '/admin/activity', label: 'Activity', icon: Activity },
  { path: '/admin/secret-findings', label: 'Secret Findings', icon: AlertTriangle },
  { path: '/admin/platform-settings', label: 'Platform Settings', icon: Sliders },
  { path: '/admin/templates', label: 'Templates', icon: LayoutTemplate },
  { path: '/admin/resource-limits', label: 'Resource Limits', icon: Server },
  { path: '/admin/claude-code', label: 'Claude Code', icon: Bot },
  { path: '/admin/analytics', label: 'Analytics', icon: BarChart3 },
  { path: '/admin/analytics/system', label: 'System Health', icon: Activity },
]

export default function Layout({ children }: LayoutProps) {
  const user = useUser()
  const location = useLocation()
  const [stoppingImpersonation, setStoppingImpersonation] = useState(false)

  const {
    isMobile,
    isCollapsed,
    isOpen,
    toggleCollapsed,
    closeSidebar,
    openSidebar,
  } = useSidebarState()

  async function handleStopImpersonating() {
    setStoppingImpersonation(true)
    try {
      await stopImpersonating()
    } catch (err) {
      console.error('Failed to stop impersonating:', err)
      setStoppingImpersonation(false)
    }
  }

  function handleNavClick() {
    if (isMobile) {
      closeSidebar()
    }
  }

  const { unseenCount } = useWhatsNew()
  const { showWelcome } = useWelcome()
  const isImpersonating = user.impersonation?.impersonating
  const showLabels = isMobile || !isCollapsed

  const renderNavItem = (item: { path: string; label: string; icon: React.ElementType }) => {
    const isActive = location.pathname === item.path
    const showChangelogBadge = item.path === '/changelog' && unseenCount > 0
    const link = (
      <Link
        to={item.path}
        onClick={handleNavClick}
        title={!showLabels ? item.label : undefined}
        className={cn(
          'flex items-center gap-3 px-3 py-2 rounded-lg transition-colors',
          isActive
            ? 'bg-primary text-primary-foreground'
            : 'hover:bg-secondary',
          !showLabels && 'justify-center'
        )}
      >
        <item.icon className="w-4 h-4 flex-shrink-0" />
        {showLabels && <span>{item.label}</span>}
        {showChangelogBadge && showLabels && (
          <span className={cn(
            'ml-auto px-1.5 py-0.5 text-xs font-medium rounded-full min-w-[20px] text-center',
            isActive
              ? 'bg-primary-foreground text-primary'
              : 'bg-primary text-primary-foreground'
          )}>
            {unseenCount}
          </span>
        )}
      </Link>
    )
    return (
      <li key={item.path} className="relative">
        {link}
        {showChangelogBadge && !showLabels && (
          <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-primary rounded-full" />
        )}
      </li>
    )
  }

  return (
    <div className="flex h-screen bg-background overflow-hidden">
      {/* Mobile overlay backdrop */}
      {isMobile && isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 transition-opacity"
          onClick={closeSidebar}
        />
      )}

      {/* Sidebar - fixed height, internal scroll */}
      <aside
        className={cn(
          'border-r border-border bg-card flex flex-col transition-all duration-300 ease-in-out z-50 overflow-hidden',
          isMobile
            ? 'fixed left-0 top-0 bottom-0 w-64'
            : 'h-screen',
          !isMobile && (isCollapsed ? 'w-16' : 'w-64'),
          isMobile && !isOpen && '-translate-x-full'
        )}
      >
        {/* Fixed Header - Logo */}
        <div className={cn(
          'flex items-center justify-between border-b border-border flex-shrink-0',
          showLabels
            ? (isMobile
                ? 'px-4 pb-4 pt-[calc(1rem+env(safe-area-inset-top))]'
                : 'p-4')
            : 'p-3'
        )}>
          {showLabels ? (
            <Link to="/" onClick={handleNavClick} className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center flex-shrink-0">
                <ConciergeBell className="w-5 h-5 text-primary-foreground" strokeWidth={2.5} aria-hidden="true" />
              </div>
              <span className="font-semibold text-lg">Code Concierge</span>
              <BetaBadge />
            </Link>
          ) : (
            <Link to="/" className="flex items-center justify-center w-full">
              <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center" aria-label="Code Concierge">
                <ConciergeBell className="w-5 h-5 text-primary-foreground" strokeWidth={2.5} aria-hidden="true" />
              </div>
            </Link>
          )}

          {/* Mobile close button */}
          {isMobile && (
            <button
              onClick={closeSidebar}
              className="p-1.5 rounded-lg hover:bg-secondary transition-colors"
              aria-label="Close sidebar"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Scrollable Navigation */}
        <nav className={cn('flex-1 overflow-y-auto min-h-0 p-4 space-y-6', !showLabels && 'px-2')}>
          <div>
            {showLabels && (
              <p className="text-xs font-medium text-muted-foreground mb-2">NAVIGATION</p>
            )}
            <ul className="space-y-1">
              {navItems.map(renderNavItem)}
            </ul>
          </div>

          <div>
            {showLabels && (
              <p className="text-xs font-medium text-muted-foreground mb-2">DOCS</p>
            )}
            <ul className="space-y-1">
              {docsItems.map(renderNavItem)}
            </ul>
          </div>

          {user.isAdmin && (
            <div>
              {showLabels && (
                <p className="text-xs font-medium text-muted-foreground mb-2">ADMIN</p>
              )}
              <ul className="space-y-1">
                {adminNavItems.map(renderNavItem)}
              </ul>
            </div>
          )}

          {/* Support */}
          <div>
            {showLabels && (
              <p className="text-xs font-medium text-muted-foreground mb-2">SUPPORT</p>
            )}
            <ul className="space-y-1">
              {renderNavItem({ path: '/feedback', label: 'Feedback', icon: Lightbulb })}
              <li>
                {!showLabels ? (
                  <button
                    type="button"
                    title="Ask the AI chatbot"
                    aria-label="Ask the AI chatbot"
                    onClick={() => openHelpChat()}
                    className={cn(
                      'w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors hover:bg-secondary',
                      'justify-center'
                    )}
                  >
                    <MessageCircle className="w-4 h-4 flex-shrink-0" />
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => openHelpChat()}
                    className="w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors hover:bg-secondary text-left"
                  >
                    <MessageCircle className="w-4 h-4 flex-shrink-0" />
                    <div className="flex flex-col">
                      <span>AI Chatbot</span>
                      <span className="text-xs text-muted-foreground">Ask questions, get instant help</span>
                    </div>
                  </button>
                )}
              </li>
            </ul>
          </div>

          {/* News */}
          {showLabels && (
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-2">NEWS</p>
              <div className="flex items-start gap-3 px-3 py-2 rounded-lg bg-secondary/50">
                <Info className="w-4 h-4 flex-shrink-0 mt-0.5 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">
                  To access CRM or Sales Mind, please{' '}
                  <Link
                    to="/settings"
                    onClick={handleNavClick}
                    className="text-primary hover:underline"
                  >
                    link your internal account
                  </Link>
                  .
                </p>
              </div>
            </div>
          )}
        </nav>

        {/* Fixed Footer - User Info & Actions */}
        <div className={cn('border-t border-border flex-shrink-0', showLabels ? 'p-4' : 'p-2')}>
          {showLabels ? (
            <>
              {/* User Info Row */}
              <div className="flex items-center gap-3 mb-3">
                <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-medium">
                    {user.firstName?.charAt(0).toUpperCase() || user.name?.charAt(0).toUpperCase() || user.email.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">
                    {user.firstName && user.lastName
                      ? `${user.firstName} ${user.lastName}`
                      : user.firstName || user.name}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                </div>
              </div>

              {/* Actions Row */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1">
                  <NotificationBell />
                  <ThemeToggleIcon />
                  {!isMobile && (
                      <button title="Collapse the sidebar to icon-only view"
                        onClick={toggleCollapsed}
                        className="p-2 rounded-lg hover:bg-secondary transition-colors text-muted-foreground hover:text-foreground"
                      >
                        <PanelLeftClose className="w-4 h-4" />
                      </button>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {user.isAdmin && (
                    <span className="px-2 py-0.5 text-xs font-medium bg-amber-500/20 text-amber-500 rounded">
                      Admin
                    </span>
                  )}
                  <a title="Sign out of your account"
                    href="/auth/logout"
                    className="p-2 rounded-lg hover:bg-secondary transition-colors text-muted-foreground hover:text-foreground"
                  >
                    <LogOut className="w-4 h-4" />
                  </a>
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Collapsed: User Avatar with Admin indicator */}
              <div className="flex justify-center mb-2">
                <div className="relative">
                  <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center">
                    <span className="text-sm font-medium">
                      {user.firstName?.charAt(0).toUpperCase() || user.name?.charAt(0).toUpperCase() || user.email.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  {user.isAdmin && (
                    <div title="You have admin access to platform settings" className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-amber-500 rounded-full border-2 border-card" />
                  )}
                </div>
              </div>

              {/* Collapsed: Action Icons */}
              <div className="flex flex-col items-center gap-1">
                <NotificationBell />
                <ThemeToggleIcon />
                <button title="Expand the sidebar to show labels"
                  onClick={toggleCollapsed}
                  className="p-2 rounded-lg hover:bg-secondary transition-colors text-muted-foreground hover:text-foreground"
                >
                  <PanelLeft className="w-4 h-4" />
                </button>
                <a title="Sign out of your account"
                  href="/auth/logout"
                  className="p-2 rounded-lg hover:bg-secondary transition-colors text-muted-foreground hover:text-foreground"
                >
                  <LogOut className="w-4 h-4" />
                </a>
              </div>
            </>
          )}
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto flex flex-col min-w-0 bg-gray-50 dark:bg-background">
        {/* Mobile Header */}
        {isMobile && (
          <div className="sticky top-0 z-30 flex items-center justify-between px-4 pb-3 pt-[calc(0.75rem+env(safe-area-inset-top))] border-b border-border bg-card flex-shrink-0">
            <div className="flex items-center gap-4">
              <button
                onClick={openSidebar}
                className="p-2 rounded-lg hover:bg-secondary transition-colors"
                aria-label="Open menu"
              >
                <Menu className="w-5 h-5" />
              </button>
              <Link to="/" className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-primary flex items-center justify-center" aria-label="Code Concierge">
                  <ConciergeBell className="w-4 h-4 text-primary-foreground" strokeWidth={2.5} aria-hidden="true" />
                </div>
                <span className="font-semibold">Code Concierge</span>
                <BetaBadge />
              </Link>
            </div>
            <NotificationBell />
          </div>
        )}

        {/* Impersonation Banner */}
        {isImpersonating && (
          <div className="bg-amber-500 text-black px-4 py-2 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              <span className="text-sm font-medium">
                Viewing as <strong>{user.email}</strong>
                <span className="ml-1 opacity-75">
                  (impersonated by {user.impersonation?.originalAdmin})
                </span>
              </span>
            </div>
            <button
              onClick={handleStopImpersonating}
              disabled={stoppingImpersonation}
              className="flex items-center gap-1 px-3 py-1 rounded bg-black/20 hover:bg-black/30 transition-colors text-sm font-medium disabled:opacity-50"
            >
              {stoppingImpersonation ? (
                'Returning...'
              ) : (
                <>
                  <X className="w-3 h-3" />
                  Stop Impersonating
                </>
              )}
            </button>
          </div>
        )}

        <div className="flex-1 overflow-auto">
          <div className="container mx-auto p-4 sm:p-8 max-w-6xl">{children}</div>
        </div>
      </main>
      {!showWelcome && <WhatsNewDialog />}
      <WelcomeModal />
      <KeyboardShortcutsDialog />
      <HelpWidget />
      <InstallPromptBanner />
      <UpdateBanner />
    </div>
  )
}
